import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription, of } from 'rxjs';
import { switchMap, takeWhile, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { Utils } from '../../core/services/utils';

interface DeviceAuthorizationResponse {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  interval: number;
}

interface DeviceTokenResponse {
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  userName?: string;
  companyGuid?: string;
  userCompanyGuid?: string;
  error?: string;
  errorDescription?: string;
}

@Component({
  selector: 'tv-auth',
  templateUrl: './tv-auth.component.html',
  styleUrls: ['./tv-auth.component.scss'],
  standalone: false
})
export class TvAuthComponent implements OnInit, OnDestroy {
  userCode: string = '';
  verificationUri: string = '';
  deviceCode: string = '';
  expiresIn: number = 0;
  pollingInterval: number = 5;

  isLoading: boolean = true;
  isPolling: boolean = false;
  isAuthorized: boolean = false;
  errorMessage: string = '';
  timeRemaining: number = 0;

  private pollingSubscription: Subscription | null = null;
  private timerSubscription: Subscription | null = null;
  private companyGuid: string = '';
  private redirectUrl: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.companyGuid = this.route.snapshot.queryParamMap.get('companyGuid') || '';
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || '/admin/dashboardv2';

    if (!this.companyGuid) {
      this.errorMessage = 'Company GUID is required. Please provide companyGuid in the URL.';
      this.isLoading = false;
      return;
    }

    this.initiateDeviceAuthorization();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopTimer();
  }

  private initiateDeviceAuthorization(): void {
    const url = `${environment.apiInfo.apiBaseUrl}api/Auth/device/authorize?companyGuid=${encodeURIComponent(this.companyGuid)}`;

    this.http.post<DeviceAuthorizationResponse>(url, {}).subscribe({
      next: (response) => {
        this.deviceCode = response.deviceCode;
        this.userCode = response.userCode;
        this.verificationUri = response.verificationUri;
        this.expiresIn = response.expiresIn;
        this.pollingInterval = response.interval;
        this.timeRemaining = response.expiresIn;
        this.isLoading = false;

        this.startPolling();
        this.startTimer();
      },
      error: (error) => {
        this.errorMessage = error.error?.message || error.error || 'Failed to initiate device authorization. Please try again.';
        this.isLoading = false;
      },
    });
  }

  private startPolling(): void {
    this.isPolling = true;

    this.pollingSubscription = interval(this.pollingInterval * 1000)
      .pipe(
        takeWhile(() => this.isPolling && this.timeRemaining > 0),
        switchMap(() => this.pollForToken())
      )
      .subscribe();
  }

  private pollForToken() {
    const url = `${environment.apiInfo.apiBaseUrl}api/Auth/device/token?deviceCode=${encodeURIComponent(this.deviceCode)}`;

    return this.http.post<DeviceTokenResponse>(url, {}).pipe(
      switchMap((response) => {
        if (response.accessToken) {
          this.handleSuccessfulAuthorization(response);
          return of(null);
        }

        if (response.error === 'authorization_pending') {
          return of(null);
        }

        if (response.error === 'slow_down') {
          this.pollingInterval += 1;
          return of(null);
        }

        if (response.error === 'expired_token') {
          this.handleExpired();
          return of(null);
        }

        if (response.error) {
          this.errorMessage = response.errorDescription || 'Authorization failed.';
          this.stopPolling();
        }

        return of(null);
      }),
      catchError((error) => {
        if (error.status === 400 && error.error?.error === 'expired_token') {
          this.handleExpired();
        }
        return of(null);
      })
    );
  }

  private handleSuccessfulAuthorization(response: DeviceTokenResponse): void {
    this.isAuthorized = true;
    this.stopPolling();
    this.stopTimer();

    const jwtPayload = Utils.parseJwt(response.accessToken);

    this.localStorageService.authorizationData = {
      token: response.accessToken,
      refreshToken: response.refreshToken,
      userName: response.userName,
      companyGuid: response.companyGuid,
      userCompanyGuid: response.userCompanyGuid,
      companies: [],
      expiresAt: Utils.getTokenExpireDate(jwtPayload.exp),
      rememberMe: false,
    };

    // Mark this session as TV-auth so downstream services skip browser permission requests
    try {
      localStorage.setItem('lodasoft_tv_auth_session', 'true');
    } catch (e) {
      // localStorage may not be available
    }

    setTimeout(() => {
      this.router.navigateByUrl(this.redirectUrl);
    }, 1500);
  }

  private handleExpired(): void {
    this.errorMessage = 'The authorization code has expired. Please refresh the page to get a new code.';
    this.stopPolling();
    this.stopTimer();
  }

  private startTimer(): void {
    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.timeRemaining > 0) {
        this.timeRemaining--;
      } else {
        this.handleExpired();
      }
    });
  }

  private stopPolling(): void {
    this.isPolling = false;
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  refreshCode(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.stopPolling();
    this.stopTimer();
    this.initiateDeviceAuthorization();
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}
